import React, { useState, useMemo } from 'react';
import styles from './AdvancedDoctorFilters.module.css';

const AdvancedDoctorFilters = ({ doctors }) => {
    const [rateFilter, setRateFilter] = useState({ count: 4, period: 'week' });
    const [activityFilter, setActivityFilter] = useState({ hospital: '', days: 30 });

    const handleRateChange = (e) => setRateFilter({ ...rateFilter, [e.target.name]: e.target.value });
    const handleActivityChange = (e) => setActivityFilter({ ...activityFilter, [e.target.name]: e.target.value });

    const hospitalOptions = useMemo(() => {
        if (!doctors) return [];
        const names = doctors.map(doc => doc.hospital.name);
        return [...new Set(names)].sort();
    }, [doctors]);

    const filteredByRate = useMemo(() => {
        if (!doctors) return [];
        const periodMultipliers = {
            day: 1,
            week: 7,
            month: 30.44,
            six_months: 182.5,
            year: 365
        };

        return doctors.filter(doc => {
            if (doc.referralHistory.length < 2) return false;

            const firstReferral = new Date(doc.referralHistory[doc.referralHistory.length - 1].createdAt);
            const lastReferral = new Date(doc.lastReferralDate);
            const durationDays = (lastReferral - firstReferral) / (1000 * 60 * 60 * 24);
            
            if (durationDays <= 0) return false;

            const avgTestsPerDay = doc.totalTestsReferred / durationDays;
            const avgTestsPerPeriod = avgTestsPerDay * periodMultipliers[rateFilter.period];

            return avgTestsPerPeriod > rateFilter.count;
        });
    }, [doctors, rateFilter]);

    const filteredByActivity = useMemo(() => {
        if (!doctors || !activityFilter.hospital || !activityFilter.days) {
            return [];
        }

        const daysToSubtract = parseInt(activityFilter.days, 10);
        if (isNaN(daysToSubtract)) {
            return [];
        }

        const thresholdTimestamp = Date.now() - (daysToSubtract * 24 * 60 * 60 * 1000);

        const results = doctors.filter(doc => {
            const matchesHospital = doc.hospital && doc.hospital.name && 
                                    doc.hospital.name === activityFilter.hospital;

            if (!matchesHospital) {
                return false;
            }

            return doc.referralHistory.some(ref => new Date(ref.createdAt).getTime() > thresholdTimestamp);
        });

        return results.map(doc => {
            const testsInPeriod = doc.referralHistory.filter(ref => new Date(ref.createdAt).getTime() > thresholdTimestamp).length;
            return {
                ...doc,
                testsInPeriod
            };
        });
    }, [doctors, activityFilter]);

    return (
        <div className={styles.container}>
            {/* Section 1: Filter by Referral Rate */}
            <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>Filter by Referral Rate</h3>
                <div className={styles.controls}>
                    <span>Show doctors with an average of more than</span>
                    <input type="number" name="count" value={rateFilter.count} onChange={handleRateChange} className={styles.input} />
                    <span>tests per</span>
                    <select name="period" value={rateFilter.period} onChange={handleRateChange} className={styles.select}>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="six_months">6 Months</option>
                        <option value="year">Year</option>
                    </select>
                </div>
                <table className={styles.resultsTable}>
                    <thead>
                        <tr>
                            <th>Doctor Name</th>
                            <th>Hospital</th>
                            <th>Total Tests</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredByRate.map(doc => (
                            <tr key={doc.doctor_id}>
                                <td>{doc.doctor_name}</td>
                                <td>{doc.hospital.name}</td>
                                <td>{doc.totalTestsReferred}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Section 2: Filter by Recent Hospital Activity */}
            <div className={styles.filterSection}>
                <h3 className={styles.sectionTitle}>Filter by Recent Hospital Activity</h3>
                <div className={styles.activityControls}>
                    <div className={styles.controlGroup}>
                        <label>Show doctors from hospital</label>
                        <select name="hospital" value={activityFilter.hospital} onChange={handleActivityChange} className={styles.select}>
                            <option value="">Select a Hospital</option>
                            {hospitalOptions.map(name => <option key={name} value={name}>{name}</option>)}
                        </select>
                    </div>
                    <div className={styles.controlGroup}>
                        <label>who referred tests in the last</label>
                        <input type="number" name="days" value={activityFilter.days} onChange={handleActivityChange} className={styles.input} />
                        <label>days.</label>
                    </div>
                </div>
                <table className={styles.resultsTable}>
                     <thead>
                        <tr>
                            <th>Doctor Name</th>
                            <th>Hospital</th>
                            <th>Tests in Period</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredByActivity.map(doc => (
                            <tr key={doc.doctor_id}>
                                <td>{doc.doctor_name}</td>
                                <td>{doc.hospital.name}</td>
                                <td>{doc.testsInPeriod}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdvancedDoctorFilters;
